import { type RefObject } from 'react';
import type { SaveStatus } from '@/hooks/useChinWorkspacePersistence';
import { cn, CHIN_TIMEFRAME, type CtraderAccount, type SymbolList } from './types';
import SyncStatusDot from './ChinSyncStatusDot';

interface ChinMonitorTopBarProps {
  symbolLists: SymbolList[];
  loadingLists: boolean;
  selectedListId: string;
  selectedList: SymbolList | null;
  dropdownOpen: boolean;
  onToggleDropdown: () => void;
  dropdownRef: RefObject<HTMLDivElement | null>;
  onSelectList: (list: SymbolList) => void;
  currentAccount: CtraderAccount | undefined;
  selectedAccountId: string;
  connected: boolean;
  dailyResetFiredToday: boolean;
  dailyResetFiredAt: string;
  dailyCountdown: string;
  onResetAll: () => void;
  onForceCheckAll: () => void;
  forceCheckRunning: boolean;
  showActivityLog: boolean;
  onToggleActivityLog: () => void;
  activityLogCount: number;
  tma50AlertOn: boolean;
  convergenceAlertOn: boolean;
  boxVsTma50AlertOn: boolean;
  onToggleTma50Alert: () => void;
  onToggleConvergenceAlert: () => void;
  onToggleBoxVsTma50Alert: () => void;
  rule4Enabled: boolean;
  onToggleRule4: () => void;
  saveStatus: SaveStatus;
  testTelegramRunning: boolean;
  testTelegramResult: { success: boolean; error?: string } | null;
  onTestTelegram: () => void;
  isEngineRunning: boolean;
  isLoading: boolean;
  loadedCount: number;
  symbolCount: number;
}

export default function ChinMonitorTopBar({
  symbolLists,
  loadingLists,
  selectedListId,
  selectedList,
  dropdownOpen,
  onToggleDropdown,
  dropdownRef,
  onSelectList,
  currentAccount,
  selectedAccountId,
  connected,
  dailyResetFiredToday,
  dailyResetFiredAt,
  dailyCountdown,
  onResetAll,
  onForceCheckAll,
  forceCheckRunning,
  showActivityLog,
  onToggleActivityLog,
  activityLogCount,
  tma50AlertOn,
  convergenceAlertOn,
  boxVsTma50AlertOn,
  onToggleTma50Alert,
  onToggleConvergenceAlert,
  onToggleBoxVsTma50Alert,
  rule4Enabled,
  onToggleRule4,
  saveStatus,
  testTelegramRunning,
  testTelegramResult,
  onTestTelegram,
  isEngineRunning,
  isLoading,
  loadedCount,
  symbolCount,
}: ChinMonitorTopBarProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      {/* Left: Symbol List Dropdown */}
      <div className="flex items-center gap-3">
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={onToggleDropdown}
            className={cn(
              'flex items-center gap-3 px-4 py-2.5 border rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap min-w-[220px]',
              selectedList
                ? 'bg-background-50 border-background-300/60 hover:border-background-400/60 text-foreground-900'
                : 'bg-primary-500 border-primary-500 text-background-50 hover:bg-primary-600',
              dropdownOpen && 'ring-2 ring-primary-500/20 border-primary-500/50'
            )}
          >
            <i className={cn(
              'text-lg',
              selectedList ? 'ri-list-check-3 text-foreground-500' : 'ri-add-line'
            )}></i>
            <div className="flex-1 text-left">
              {selectedList ? (
                <>
                  <span className="font-semibold block">{selectedList.name}</span>
                  <span className="text-[10px] opacity-60 block">{selectedList.symbols.length} symbols</span>
                </>
              ) : (
                <span className="font-semibold">Select Symbol List</span>
              )}
            </div>
            <i className={cn(
              'text-sm transition-transform',
              dropdownOpen ? 'ri-arrow-up-s-line rotate-0' : 'ri-arrow-down-s-line',
              selectedList ? 'text-foreground-400' : 'text-background-50/70'
            )}></i>
          </button>

          {/* Dropdown Menu */}
          {dropdownOpen && (
            <div className="absolute top-full mt-1.5 left-0 w-full bg-background-50 border border-background-200/70 rounded-xl shadow-lg z-30 overflow-hidden">
              <div className="max-h-64 overflow-y-auto">
                {loadingLists ? (
                  <div className="px-4 py-6 text-center">
                    <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-xs text-foreground-400">Loading lists...</p>
                  </div>
                ) : symbolLists.length === 0 ? (
                  <div className="px-4 py-6 text-center">
                    <i className="ri-emotion-sad-line text-foreground-300 text-xl block mb-2"></i>
                    <p className="text-xs text-foreground-400">No symbol lists created yet</p>
                  </div>
                ) : (
                  symbolLists.map((list) => (
                    <button
                      key={list.id}
                      type="button"
                      onClick={() => onSelectList(list)}
                      className={cn(
                        'w-full text-left px-4 py-3 flex items-center gap-3 transition-colors cursor-pointer',
                        selectedListId === list.id
                          ? 'bg-primary-500/10 text-primary-600'
                          : 'hover:bg-background-100 text-foreground-700'
                      )}
                    >
                      <div className={cn(
                        'w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
                        selectedListId === list.id
                          ? 'bg-primary-500/20'
                          : 'bg-background-200/70'
                      )}>
                        <i className={cn(
                          'text-sm',
                          selectedListId === list.id
                            ? 'ri-check-line text-primary-500'
                            : 'ri-list-check-2 text-foreground-400'
                        )}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium block truncate">{list.name}</span>
                        <span className="text-[10px] text-foreground-400">{list.symbols.length} symbols</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Account display */}
        {currentAccount && selectedAccountId && (
          <div className="hidden sm:inline-flex items-center gap-2 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg">
            <div className={cn(
              'w-2 h-2 rounded-full shrink-0',
              connected ? 'bg-emerald-400 animate-pulse' : 'bg-foreground-300'
            )}></div>
            <span className="text-[10px] text-foreground-500">Account:</span>
            <span className="text-xs font-semibold text-foreground-800 whitespace-nowrap">
              {currentAccount.account_name}
            </span>
            <span className="text-[10px] text-foreground-400">#{selectedAccountId}</span>
          </div>
        )}

        {/* Timeframe badge */}
        {selectedList && (
          <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 bg-accent-500/10 text-accent-500 rounded-full text-[10px] font-medium">
            <i className="ri-time-line"></i>
            {CHIN_TIMEFRAME}
          </span>
        )}
      </div>

      {/* Right: Status */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* ─── Risk Control Rule 4 Toggle ─── */}
        {selectedList && (
          <button
            type="button"
            onClick={onToggleRule4}
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all cursor-pointer whitespace-nowrap',
              rule4Enabled
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                : 'bg-background-100 border-background-300/60 text-foreground-400 hover:border-foreground-400/60'
            )}
            title={rule4Enabled
              ? 'Risk Control Rule 4 (After Hours): auto-shutdown all engines at 23:00 GMT+7 daily — click to disable'
              : 'Risk Control Rule 4 (After Hours) DISABLED — engines will NOT auto-shutdown at 23:00 GMT+7'}
          >
            <div className={cn('w-4 h-4 flex items-center justify-center', rule4Enabled ? 'text-emerald-400' : 'text-foreground-400')}>
              <i className="ri-moon-line text-[11px]"></i>
            </div>
            <span>Rule 4</span>
            <span className={cn(
              'px-1 rounded text-[8px] font-bold leading-4',
              rule4Enabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-background-200/70 text-foreground-500'
            )}>
              {rule4Enabled ? 'ON' : 'OFF'}
            </span>
            <SyncStatusDot status={saveStatus} />
          </button>
        )}
        {/* ─── Rule 4 Daily Reset Indicator ─── */}
        {selectedList && (
          <div
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border transition-colors',
              !rule4Enabled
                ? 'bg-background-100 border-background-300/60 text-foreground-400'
                : dailyResetFiredToday
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                  : 'bg-background-100 border-background-300/60 text-foreground-500'
            )}
            title={!rule4Enabled
              ? 'Rule 4 disabled — no daily auto-shutdown'
              : dailyResetFiredToday
                ? `Rule 4: Daily reset fired at ${dailyResetFiredAt}. Next reset in ${dailyCountdown}`
                : `Rule 4: Daily reset at 23:00 GMT+7 (${dailyCountdown} remaining)`
            }
          >
            <div className={cn(
              'w-4 h-4 flex items-center justify-center',
              !rule4Enabled ? 'text-foreground-400' : dailyResetFiredToday ? 'text-emerald-400' : 'text-foreground-400'
            )}>
              {!rule4Enabled ? (
                <i className="ri-moon-clear-line text-[11px]"></i>
              ) : dailyResetFiredToday ? (
                <i className="ri-check-double-line text-[11px]"></i>
              ) : (
                <i className="ri-timer-line text-[11px]"></i>
              )}
            </div>
            <span className="font-mono text-[10px]">
              {!rule4Enabled ? 'OFF' : dailyResetFiredToday ? 'Done' : dailyCountdown}
            </span>
            {rule4Enabled && dailyResetFiredToday && (
              <span className="text-[9px] opacity-70">{dailyResetFiredAt}</span>
            )}
          </div>
        )}
        {/* ─── Reset All Button ─── */}
        {selectedList && (
          <button
            type="button"
            onClick={onResetAll}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all cursor-pointer whitespace-nowrap bg-rose-500/10 border-rose-500/30 text-rose-400 hover:bg-rose-500/20 hover:text-rose-300"
            title="Clear play setup (price, max, direction) for ALL symbols in this list"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-delete-bin-5-line text-[11px]"></i>
            </div>
            <span>Reset All</span>
          </button>
        )}
        {/* ─── Force Check All Button ─── */}
        {selectedList && (
          <button
            type="button"
            onClick={onForceCheckAll}
            disabled={forceCheckRunning}
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all cursor-pointer whitespace-nowrap',
              forceCheckRunning
                ? 'bg-background-100 border-background-300/60 text-foreground-400 cursor-wait'
                : 'bg-background-50 border-background-300/60 text-foreground-600 hover:border-accent-500/40 hover:text-accent-500 hover:bg-accent-500/5'
            )}
            title="Force check TMA50 cross & MA convergence on all symbols"
          >
            <div className={cn(
              'w-4 h-4 flex items-center justify-center',
              forceCheckRunning ? 'text-foreground-400' : 'text-foreground-500'
            )}>
              {forceCheckRunning ? (
                <div className="w-2.5 h-2.5 border-2 border-foreground-400 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <i className="ri-flashlight-line text-[11px]"></i>
              )}
            </div>
            <span>
              {forceCheckRunning
                ? (isLoading ? 'Refreshing history...' : 'Checking...')
                : 'Force Check'}
            </span>
          </button>
        )}
        {/* ─── Activity Log Toggle ─── */}
        {selectedList && (
          <button
            type="button"
            onClick={onToggleActivityLog}
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all cursor-pointer whitespace-nowrap',
              showActivityLog
                ? 'bg-accent-500/10 border-accent-500/30 text-accent-400'
                : 'bg-background-50 border-background-300/60 text-foreground-600 hover:border-accent-500/40 hover:text-accent-500 hover:bg-accent-500/5'
            )}
            title="View activity log — see what the order engine is doing"
          >
            <div className={cn(
              'w-4 h-4 flex items-center justify-center',
              showActivityLog ? 'text-accent-400' : 'text-foreground-500'
            )}>
              <i className="ri-terminal-box-line text-[11px]"></i>
            </div>
            <span>Activity Log</span>
            {activityLogCount > 0 && (
              <span className={cn(
                'text-[9px] font-mono px-1 rounded-full',
                showActivityLog ? 'bg-accent-500/20 text-accent-400' : 'bg-background-200/70 text-foreground-500'
              )}>
                {activityLogCount}
              </span>
            )}
          </button>
        )}
        {/* ─── Telegram Alert Toggles (TMA50 / Tụ Tách) ─── */}
        {selectedList && (
          <button
            type="button"
            onClick={onToggleTma50Alert}
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all cursor-pointer whitespace-nowrap',
              tma50AlertOn
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                : 'bg-background-100 border-background-300/60 text-foreground-400 hover:border-foreground-400/60'
            )}
            title={tma50AlertOn
              ? 'TMA50 Cross Telegram alerts ON — click to mute (engine keeps running)'
              : 'TMA50 Cross Telegram alerts MUTED — engine still detecting, no message sent'}
          >
            <div className={cn('w-4 h-4 flex items-center justify-center', tma50AlertOn ? 'text-emerald-400' : 'text-foreground-400')}>
              <i className="ri-line-chart-line text-[11px]"></i>
            </div>
            <span>TMA50</span>
            <span className={cn(
              'px-1 rounded text-[8px] font-bold leading-4',
              tma50AlertOn ? 'bg-emerald-500/20 text-emerald-300' : 'bg-background-200/70 text-foreground-500'
            )}>
              {tma50AlertOn ? 'ON' : 'OFF'}
            </span>
            <SyncStatusDot status={saveStatus} />
          </button>
        )}
        {selectedList && (
          <button
            type="button"
            onClick={onToggleConvergenceAlert}
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all cursor-pointer whitespace-nowrap',
              convergenceAlertOn
                ? 'bg-accent-500/10 border-accent-500/30 text-accent-400'
                : 'bg-background-100 border-background-300/60 text-foreground-400 hover:border-foreground-400/60'
            )}
            title={convergenceAlertOn
              ? 'MA Convergence (Tụ Tách) Telegram alerts ON — click to mute (engine keeps running)'
              : 'MA Convergence (Tụ Tách) Telegram alerts MUTED — engine still detecting, no message sent'}
          >
            <div className={cn('w-4 h-4 flex items-center justify-center', convergenceAlertOn ? 'text-accent-400' : 'text-foreground-400')}>
              <i className="ri-arrow-left-right-line text-[11px]"></i>
            </div>
            <span>&gt;&lt;</span>
            <span className={cn(
              'px-1 rounded text-[8px] font-bold leading-4',
              convergenceAlertOn ? 'bg-accent-500/20 text-accent-300' : 'bg-background-200/70 text-foreground-500'
            )}>
              {convergenceAlertOn ? 'ON' : 'OFF'}
            </span>
            <SyncStatusDot status={saveStatus} />
          </button>
        )}
        {selectedList && (
          <button
            type="button"
            onClick={onToggleBoxVsTma50Alert}
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all cursor-pointer whitespace-nowrap',
              boxVsTma50AlertOn
                ? 'bg-secondary-500/10 border-secondary-500/30 text-secondary-500'
                : 'bg-background-100 border-background-300/60 text-foreground-400 hover:border-foreground-400/60'
            )}
            title={boxVsTma50AlertOn
              ? 'Box vs TMA50 Telegram alerts ON — click to mute (engine keeps running)'
              : 'Box vs TMA50 Telegram alerts MUTED — engine still detecting, no message sent'}
          >
            <div className={cn('w-4 h-4 flex items-center justify-center', boxVsTma50AlertOn ? 'text-secondary-500' : 'text-foreground-400')}>
              <i className="ri-stack-line text-[11px]"></i>
            </div>
            <span>Box·TMA50</span>
            <span className={cn(
              'px-1 rounded text-[8px] font-bold leading-4',
              boxVsTma50AlertOn ? 'bg-secondary-500/20 text-secondary-500' : 'bg-background-200/70 text-foreground-500'
            )}>
              {boxVsTma50AlertOn ? 'ON' : 'OFF'}
            </span>
            <SyncStatusDot status={saveStatus} />
          </button>
        )}
        {/* ─── Test Telegram Button ─── */}
        {selectedList && (
          <button
            type="button"
            onClick={onTestTelegram}
            disabled={testTelegramRunning}
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all cursor-pointer whitespace-nowrap',
              testTelegramRunning
                ? 'bg-background-100 border-background-300/60 text-foreground-400 cursor-wait'
                : testTelegramResult?.success
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                  : testTelegramResult && !testTelegramResult.success
                    ? 'bg-red-500/10 border-red-500/30 text-red-400'
                    : 'bg-background-50 border-background-300/60 text-foreground-600 hover:border-accent-500/40 hover:text-accent-500 hover:bg-accent-500/5'
            )}
            title="Send test Telegram message to verify bot connection"
          >
            <div className={cn(
              'w-4 h-4 flex items-center justify-center',
              testTelegramRunning ? 'text-foreground-400' : 'text-foreground-500'
            )}>
              {testTelegramRunning ? (
                <div className="w-2.5 h-2.5 border-2 border-foreground-400 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <i className="ri-send-plane-line text-[11px]"></i>
              )}
            </div>
            <span>Test Telegram</span>
          </button>
        )}
        {/* ─── Test Telegram Result Banner ─── */}
        {testTelegramResult && (
          <div className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px]',
            testTelegramResult.success
              ? 'bg-emerald-500/10 text-emerald-400'
              : 'bg-red-500/10 text-red-400'
          )}>
            <i className={cn(
              'text-[10px]',
              testTelegramResult.success ? 'ri-check-line' : 'ri-close-line'
            )}></i>
            <span className="max-w-[200px] truncate">
              {testTelegramResult.success
                ? 'Sent!'
                : testTelegramResult.error || 'Failed'}
            </span>
          </div>
        )}
        {isEngineRunning && connected && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-[10px] font-medium">
            <i className="ri-flashlight-line"></i>
            Live
          </span>
        )}
        {isLoading && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-500/10 text-amber-400 rounded-full text-[10px] font-medium">
            <div className="w-2.5 h-2.5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
            Loading data...
          </span>
        )}
        {!selectedList && !isLoading && (
          <span className="text-[10px] text-foreground-400">
            Select a symbol list to begin
          </span>
        )}
        {isEngineRunning && symbolCount > 0 && (
          <span className="text-[10px] text-foreground-400">
            {loadedCount}/{symbolCount} loaded
          </span>
        )}
      </div>
    </div>
  );
}